import { Box, Typography } from '@mui/material';

import ApercibidosStatusIcon from '@/components/shared/icons/apercibidos-status-icon';
import CheckIcon from '@/components/shared/icons/check-icon';
import ConvocadoIcon from '@/components/shared/icons/convocado-icon';
import InfoIcon from '@/components/shared/icons/info-icon';
import NoInscritoIcon from '@/components/shared/icons/recommendation-icons/no-inscrito-icon';
import SancionadosIcon from '@/components/shared/icons/sancionados-icon';
import AvailableStatusIcon from '@/components/shared/icons/status-icons/available-status-icon';
import DudaLeveStatusIcon from '@/components/shared/icons/status-icons/duda-leve-status-icon';
import DudaStatusIcon from '@/components/shared/icons/status-icons/duda-status-icon';
import ExtradeportivoIcon from '@/components/shared/icons/status-icons/extradeportivo-icon';
import InjuredStatusIcon from '@/components/shared/icons/status-icons/injured-status-icon';
import TipsIcon from '@/components/shared/icons/tips-icon';
import { POSITION_COLORS, POSITION_NAMES } from '@/constants/color-constants';
import { LEAGUES } from '@/constants/leagues';
import { PLAYER_STATUS } from '@/constants/player-constants';

import { getFantasyPlayerImage, getMasterPlayerImage } from './images-helper';
import URL_FACTORY from './url-helper';

export const getCssClassForMarketValueDifference = (marketValue, nextMarketValue) => {
    if (!nextMarketValue) {
        return 'black-diff';
    }
    return marketValue - nextMarketValue > 0 ? 'green-diff' : 'red-diff';
};

export const getMarketValueDifference = (marketValue, nextMarketValue) => {
    if (!nextMarketValue) {
        return '-';
    }
    return (marketValue - nextMarketValue).toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.') + ' €';
};

export const getDifferenceColor = function (number) {
    if (number >= 0) {
        return 'rgb(102, 187, 106)';
    }

    return 'rgb(239, 83, 80)';
};

export const replaceDiacritis = (text) => {
    if (!text) return text;
    const diacriticsMap = {
        à: 'a',
        á: 'a',
        â: 'a',
        ã: 'a',
        ä: 'a',
        å: 'a',
        æ: 'ae',
        ç: 'c',
        č: 'c',
        ć: 'c',
        è: 'e',
        é: 'e',
        ê: 'e',
        ë: 'e',
        ì: 'i',
        í: 'i',
        î: 'i',
        ï: 'i',
        ð: 'd',
        ò: 'o',
        ó: 'o',
        ô: 'o',
        õ: 'o',
        ö: 'o',
        ø: 'o',
        ù: 'u',
        ú: 'u',
        û: 'u',
        ü: 'u',
        ý: 'y',
        þ: 'th',
        ÿ: 'y',
        ć: 'c',
    };

    return text
        .replace(/[àáâãäåæçèéêëìíîïðòóôõöøùúûüýþÿ]/g, (match) => {
            return diacriticsMap[match];
        })
        .toLowerCase();
};

export const formatPlayerName = (name) => {
    if (!name) return '';

    const parts = name.trim().split(/\s+/);

    // Caso 1: Una sola palabra
    if (parts.length === 1) return parts[0];

    const fullName = name.trim();

    // Caso 2: Ya está en formato abreviado (D. Pérez o Vini Jr.)
    if (/^[A-Z]\.\s\w+$/i.test(fullName) || /jr\.?$/i.test(fullName)) {
        return fullName;
    }

    // Caso 3: Dos palabras → abreviar la primera
    if (parts.length === 2) {
        const [first, last] = parts;
        return `${first[0]}. ${last}`;
    }

    // Caso 4: Tres o más palabras → iniciales + apellido final
    const initials = parts
        .slice(0, -1)
        .map((p) => p[0].toUpperCase())
        .join('.');
    const lastName = parts[parts.length - 1];
    return `${initials}. ${lastName}`;
};

export const getPlayerPositionColor = function (positionId) {
    switch (positionId) {
        case -1:
            return '';
        case 1:
            return 'po';
        case 2:
            return 'df';
        case 3:
            return 'mc';
        case 5:
            return 'dt';
        case 11:
            return 'to';
        default:
            return 'dl';
    }
};

export const getPlayerPositionShort = function (positionId) {
    switch (positionId) {
        case -1:
            return '';
        case 1:
            return 'PO';
        case 2:
            return 'DF';
        case 3:
            return 'MC';
        case 5:
            return 'DT';
        case 11:
            return 'TO';
        default:
            return 'DL';
    }
};

export const getPlayerPosition = (positionId) => {
    switch (positionId) {
        case 1:
        case '1':
            return 'Portero';
        case 2:
        case '2':
            return 'Defensa';
        case 3:
        case '3':
            return 'Medio';
        case 5:
        case '5':
            return 'Entrenador';
        case 4:
        case '4':
        default:
            return 'Delantero';
    }
};

export const getPlayerPositionPlural = (positionId) => {
    switch (positionId) {
        case 1:
        case '1':
            return 'Porteros';
        case 2:
        case '2':
            return 'Defensas';
        case 3:
        case '3':
            return 'Medios';
        case 5:
        case '5':
            return 'Entrenadores';
        case 4:
        case '4':
        default:
            return 'Delanteros';
    }
};

export const getPlayerStatsColor = (playerStatus) => {
    switch (playerStatus) {
        case 'Lesionado':
            return 'red';
        case 'Sancionado':
            return 'orange';
        case 'Duda':
            return 'yellow';
        case 'Disponible':
        default:
            return 'green';
    }
};

export const getPlayerStatusList = () => {
    return [
        { label: 'Disponible', value: PLAYER_STATUS.OK },
        { label: 'Lesionado', value: PLAYER_STATUS.INJURED },
        { label: 'Sancionado', value: PLAYER_STATUS.SANCTIONED },
        { label: 'Duda', value: PLAYER_STATUS.DOUBTFUL },
    ];
};

export const getPriority = () => {
    return [
        { label: 'P1', value: 1 },
        { label: 'P2', value: 2 },
        { label: 'P3', value: 3 },
        { label: 'P4', value: 4 },
        { label: 'P5', value: 5 },
    ];
};

export const getPlayerStatus = [
    { status: PLAYER_STATUS.OK, statusLevel: 1 }, // Disponible
    { status: PLAYER_STATUS.DOUBTFUL, statusLevel: 1 }, // Duda verde
    { status: PLAYER_STATUS.DOUBTFUL, statusLevel: 2 }, // Duda naranja
    { status: PLAYER_STATUS.INJURED, statusLevel: 1 }, // Cruz Roja
    { status: PLAYER_STATUS.INJURED, statusLevel: 2 }, // Cruz Negra
    { status: PLAYER_STATUS.SANCTIONED, statusLevel: 1 }, // Sancionado
    { status: PLAYER_STATUS.INJURED, statusLevel: 3 }, // Extradeportivo
];

export const getPlayerStatusLevelList = (status) => {
    if (status === PLAYER_STATUS.INJURED) {
        return [
            { label: 'Cruz Roja', value: '1' },
            { label: 'Cruz Negra', value: '2' },
            { label: 'Extradeportivo', value: '3' },
        ];
    } else if (status === PLAYER_STATUS.DOUBTFUL) {
        return [
            { label: 'Cruz Verde', value: '1' },
            { label: 'Cruz Naranja', value: '2' },
        ];
    }

    return [{ label: 'No aplica', value: '1' }];
};

export const getPlayerColor = (positionId) => {
    switch (positionId) {
        case POSITION_NAMES.PORTERO:
            return POSITION_COLORS.PORTERO;
        case POSITION_NAMES.DEFENSA:
            return POSITION_COLORS.DEFENSA;
        case POSITION_NAMES.MEDIOCAMPISTA:
            return POSITION_COLORS.MEDIOCAMPISTA;
        case POSITION_NAMES.DELANTERO:
            return POSITION_COLORS.DELANTERO;
        case POSITION_NAMES.ENTRENADOR:
            return POSITION_COLORS.ENTRENADOR;
        default:
            return '';
    }
};

export const getPlayerScoreRounded = (score) => {
    if (!score) {
        return '-';
    }
    return score.toFixed(2);
};

export const getScoreColor = (score, isAvailable, isTransparent) => {
    if (isTransparent) {
        return '#007252';
    }

    if (score === null || score === undefined || !isAvailable) {
        return 'transparent';
    }

    const scoreNumber = Number(score);

    if (scoreNumber >= 8) {
        return '#1d6fb1';
    } else if (scoreNumber >= 5) {
        return 'rgb(16, 175, 0)';
    } else if (scoreNumber >= 3) {
        return '#103b17';
    } else if (scoreNumber >= 0) {
        return 'rgb(255, 152, 0)';
    } else {
        // Lower than 0
        return 'rgb(186, 59, 59)';
    }
};

export const getMinutesColor = (score, isAvailable) => {
    if (score === null || score === undefined || !isAvailable) {
        return '#ccc';
    }

    const scoreNumber = Number(score);

    if (scoreNumber >= 90) {
        return '#1d6fb1';
    } else if (scoreNumber >= 60) {
        return '#1a892d';
    } else if (scoreNumber >= 1) {
        return '#103b17';
    } else if (scoreNumber == 0) {
        return '#8d5f09';
    } else {
        // Lower than 0
        return '#dc4141';
    }
};

export const getAvailableStatus = (status) => {
    switch (status) {
        case PLAYER_STATUS.OK:
            return 'Sí';
        case PLAYER_STATUS.DOUBTFUL:
            return 'Duda';
        case PLAYER_STATUS.INJURED:
        case PLAYER_STATUS.SUSPENDED:
            return 'No';
        default:
            return '';
    }
};

export const getAvailableStatusColor = (status) => {
    switch (status) {
        case PLAYER_STATUS.OK:
            return 'rgb(16, 175, 0)';
        case PLAYER_STATUS.DOUBTFUL:
            return 'rgb(255, 152, 0)';
        case PLAYER_STATUS.INJURED:
        case PLAYER_STATUS.SUSPENDED:
            return 'rgb(211, 47, 47)';
        default:
            return '';
    }
};

export const getPlayerName = (player) => {
    if (player?.information?.name ?? false) {
        return player.information.name;
    }

    if (player?.information?.nickname ?? false) {
        return player.information.nickname;
    }

    if (player?.fantasyPlayer?.information?.nickname ?? false) {
        return player.fantasyPlayer.information.nickname;
    }

    return player?.information?.nickname;
};

export const getPlayerPositionId = (player) => {
    return player?.information.positionId;
};

export const getPlayerTeamId = (player) => {
    return player?.information.teamId ?? player.teamId;
};

export const getPlayerImage = (player, _isFantasy, _size, width) => {
    if (player?.information?.photo) {
        return URL_FACTORY.GetUniquePlayerImage(player?.information?.photo, width);
    }
    if (player?.fantasyPlayer) {
        const id = player?.fantasyPlayer?.information?.id ?? player?.information?.id;
        return id ? getFantasyPlayerImage(id, width) : URL_FACTORY.GetPlayerTemplateImage();
    }

    if (player?.information?.nickname) {
        if (player?.information?.photo) {
            return player?.information?.photo;
        }

        return getMasterPlayerImage(player, width);
    }

    return URL_FACTORY.GetPlayerTemplateImage();
};

// Te da el estado de un jugador en formato texto
export const getPlayersStatus = (status, statusLevel) => {
    switch (status) {
        case PLAYER_STATUS.INJURED:
            switch (statusLevel) {
                case 3:
                    return 'Extradeportivo';
                case 1:
                    return 'Lesionado';
                case 2:
                    return 'Lesionado';
                default:
                    return 'Lesionado';
            }
        case PLAYER_STATUS.DOUBTFUL:
            switch (statusLevel) {
                case 2:
                    return 'Duda';
                case 1:
                default:
                    return 'Duda leve';
            }

        case PLAYER_STATUS.SANCTIONED:
            return 'Sancionado';
        case PLAYER_STATUS.CONVOCADO:
            return 'Convocado';
        case PLAYER_STATUS.NO_CONVOCADO:
            return 'Disponible';
        case PLAYER_STATUS.OK:
        default:
            return 'Disponible';
    }
};

export const getPlayerIcon = (playerStatus, statusLevel, iconWidth, iconHeight) => {
    if (!playerStatus || !isNaN(playerStatus)) {
        return <AvailableStatusIcon />;
    }

    switch (playerStatus?.toLowerCase()) {
        case PLAYER_STATUS.INJURED:
            switch (statusLevel) {
                case 2:
                case '2':
                    return (
                        <InjuredStatusIcon
                            iconHeight={iconWidth ?? 16}
                            iconWidth={iconHeight ?? 16}
                            color="#22272F"
                        />
                    );
                case 3:
                case '3':
                    return <ExtradeportivoIcon iconHeight={iconWidth ?? 18} iconWidth={iconHeight ?? 18} />;
                case 1:
                case '1':
                default:
                    return (
                        <InjuredStatusIcon
                            iconHeight={iconWidth ?? 16}
                            iconWidth={iconHeight ?? 16}
                            color="#e11d48"
                        />
                    );
            }
        case PLAYER_STATUS.SANCTIONED:
            return <SancionadosIcon iconHeight={iconWidth ?? 18} iconWidth={iconHeight ?? iconWidth ?? 16} />;
        case PLAYER_STATUS.DOUBTFUL:
            switch (statusLevel) {
                case 2:
                case '2':
                    return (
                        <DudaStatusIcon
                            iconHeight={iconWidth ?? 16}
                            iconWidth={iconHeight ?? 16}
                            color="#ea580c"
                        />
                    );
                case 1:
                case '1':
                default:
                    return (
                        <DudaLeveStatusIcon
                            iconHeight={iconWidth ?? 16}
                            iconWidth={iconHeight ?? 16}
                            color="#059669"
                        />
                    );
            }
        case PLAYER_STATUS.CONVOCADO:
            return <ConvocadoIcon height={20} width={20} />;
        case PLAYER_STATUS.NO_CONVOCADO:
            return <CheckIcon height={20} width={20} />;
        case PLAYER_STATUS.OK:
        default:
            return <AvailableStatusIcon iconWidth={iconWidth} iconHeight={iconHeight} />;
    }
};

export const getPlayerIconWhenIsNotOk = (playerStatus, statusLevel, iconWidth, iconHeight) => {
    if (playerStatus === PLAYER_STATUS.OK || !playerStatus) {
        return null;
    }

    return getPlayerIcon(playerStatus, statusLevel, iconWidth, iconHeight);
};

export const getReport = (p, fontSize = 14) => {
    if (p?.generalStatus?.report) {
        let report = p.generalStatus.report;

        if (report.length > 2) {
            report = report.charAt(0).toUpperCase() + report.slice(1);
        }

        return (
            <Box className="player-information-container__item-info-status">
                <TipsIcon iconWidth={16} />
                <Typography fontSize={fontSize}>{report}</Typography>
            </Box>
        );
    } else {
        return (
            <Box className="player-information-container__item-info-status" style={{ visibility: 'hidden' }}>
                <TipsIcon iconWidth={16} />
            </Box>
        );
    }
};

export const getStatusInfo = (p, fontSize = 14) => {
    if (p?.generalStatus?.status === PLAYER_STATUS.OK) {
        return (
            <Box className="player-information-container__item-info-status">
                <InfoIcon iconWidth={15} />
                <Typography fontSize={fontSize}>
                    {p?.generalStatus?.status === PLAYER_STATUS.OK && 'Disponible para competir'}
                </Typography>
            </Box>
        );
    }

    if (p?.generalStatus?.statusInfo) {
        let statusInfo = p.generalStatus.statusInfo;

        if (statusInfo.length > 2) {
            statusInfo = statusInfo.charAt(0).toUpperCase() + statusInfo.slice(1);
        }

        return (
            <Box className="player-information-container__item-info-status">
                <Box style={{ width: '15px' }}>
                    <InfoIcon iconWidth={15} iconHeight={15} />
                </Box>
                <Typography fontSize={fontSize}>{statusInfo}</Typography>
            </Box>
        );
    }
};

export const getRecommendationsIcons = (player, alternatives, league) => {
    if (!player) return null;

    if (player?.isNoInscrito && league === LEAGUES.LA_LIGA) {
        return (
            <div
                className={`lineup-selector-container__recomendaciones ${
                    alternatives ? 'lineup-selector-container__recomendaciones-alternatives' : ''
                }`}
            >
                <NoInscritoIcon />
            </div>
        );
    }
};

export const getFillColor = (semaforo) => {
    if (semaforo === 1) {
        return '#78b159';
    } else if (semaforo === 2) {
        return '#fdcb58';
    } else if (semaforo === 3) {
        return '#dd2e44';
    } else {
        return '#fff';
    }
};

export const getIsApercibidoIcon = (player, styles, league = LEAGUES.LA_LIGA) => {
    if (player?.generalStatus?.isApercibido && league === LEAGUES.LA_LIGA) {
        return (
            <div style={{ position: 'absolute', top: '54px', left: '5px', ...styles }}>
                <ApercibidosStatusIcon iconWidth={20} iconHeight={20} />
            </div>
        );
    }
};

export const getPlayerPositionName = (position) => {
    switch (position) {
        case 'Goalkeeper':
        case '1':
        case 1:
            return 'Portero';
        case 'Defender':
        case '2':
        case 2:
            return 'Defensa';
        case 'Midfielder':
        case '3':
        case 3:
            return 'Medio';
        case 'Attacker':
        case '4':
        case 4:
            return 'Delantero';
        default:
            return '';
    }
};
